import { AttachmentInfo, ESField, ESStub, FieldMappings, HitType } from '../types/chat';
import { v5 as uuidv5 } from 'uuid';
import mime from 'mime';

const EXTENSION_REGEX = new RegExp(/\..*$/);

//eslint-disable-next-line @typescript-eslint/no-explicit-any
const findField = (curField: string, fieldPath: string[], source: unknown): any | undefined => {
	if (curField) {
		let foundField;
		if (source && typeof source === 'object' && curField in source) {
			foundField = source[curField as keyof object];
		}

		// foundField could feasibly be the boolean false but that evaluates (unsurprisingly) to false in JS so this won't ever trigger
		if (foundField || foundField === false) {
			if (typeof foundField === 'object' && fieldPath.length > 0) {
				return findField(fieldPath.splice(0, 1)[0], fieldPath, foundField);
			} else if (fieldPath.length === 0) {
				return foundField;
			}
		}
	}
};

//eslint-disable-next-line @typescript-eslint/no-explicit-any
const getNestedSearchResField = (resp: ESStub, fieldName: string): any | undefined => {
	const fieldPath: string[] = fieldName.split('.');

	if (fieldPath.length > 0 && resp._source && Object.keys(resp._source).length > 0) {
		const root = fieldPath.splice(0, 1)[0];
		return findField(root, fieldPath, resp._source);
	}
};

/**
 * Get a (potentially nested) field inside of an Elasticsearch document.
 * @param doc Elasticsearch document, containing at least a _source field
 * @param fieldMappings List of mappings which contains the paths to the fields in the document
 * @param field The field to be found
 * @returns The value for the field, empty string if not found. Throws error if the mapping cannot be used to search (ie. is not a string)
 */
export const getDocField = (doc: ESStub, fieldMappings: FieldMappings, field: keyof FieldMappings): ESField => {
	let fieldName = fieldMappings[field];

	if (typeof fieldName === 'string') {
		if (fieldName.endsWith('.keyword')) {
			fieldName = fieldName.slice(0, fieldName.lastIndexOf('.keyword'));
		}

		const res = getNestedSearchResField(doc, fieldName);
		if (res || res === false) {
			return res as ESField;
		} else {
			return '';
		}
	} else {
		throw new Error(`Tried to get Elasticsearch Document Field of non-string mapping ${field}`);
	}
};

export const getMessageId = (message: ESStub, fieldMappings: FieldMappings): string => {
	const mappedId = fieldMappings.messageId;

	if (mappedId.startsWith('_') && mappedId in message) {
		return String(message[mappedId as keyof ESStub]);
	} else if (message._source && mappedId in message._source) {
		return String(message._source[mappedId]);
	} else {
		return '';
	}
};

export const getMessageHash = (message: ESStub, dedupeFields: string[]): string => {
	const dedupe: HitType = {};
	for (const dupeKey of dedupeFields) {
		if (message._source && dupeKey in message._source) {
			dedupe[dupeKey] = message._source[dupeKey];
		}
	}

	return uuidv5(JSON.stringify(Object.values(dedupe)), uuidv5.DNS);
};

const getAttachmentInfo = (attachment: object, fieldMappings: FieldMappings): AttachmentInfo | AttachmentInfo[] => {
	// sometimes attachments are arrays-of-arrays(-of arrays etc)
	if (Array.isArray(attachment)) {
		return attachment.flatMap((att) => getAttachmentInfo(att, fieldMappings));
	} else {
		const info: AttachmentInfo = {
			filePath: 'Could not get file path',
			fileName: 'Unknown Filename',
			fileType: 'Unknown',
		};

		if (fieldMappings.attachmentPath in attachment) {
			info.filePath = String(attachment[fieldMappings.attachmentPath as keyof object]);
		}
		if (fieldMappings.attachmentName in attachment) {
			info.fileName = String(attachment[fieldMappings.attachmentName as keyof object]);
		}

		// quite often the fileType isn't actually stored, so instead of many contingencies around paths existing/ not existing
		// check if the path or name include an extension (ie ends in .xyz) and use the end of it instead
		if (EXTENSION_REGEX.test(info.filePath)) {
			const fileExtension = info.filePath.substring(info.filePath.lastIndexOf('.'));
			info.fileType = mime.getExtension(fileExtension) || fileExtension.substring(1);
		} else if (EXTENSION_REGEX.test(info.fileName)) {
			const fileExtension = info.fileName.substring(info.fileName.lastIndexOf('.'));
			info.fileType = mime.getExtension(fileExtension) || fileExtension.substring(1);
		} else {
			info.fileType = 'Unknown';
		}

		return info;
	}
};

export const processAttachments = (message: ESStub, fieldMappings: FieldMappings): AttachmentInfo[] => {
	let outInfo: AttachmentInfo[] = [];
	const attachment = getDocField(message, fieldMappings, 'attachment');

	if (typeof attachment === 'object') {
		outInfo = [attachment].flatMap((att) => getAttachmentInfo(att, fieldMappings));
	} else if (Array.isArray(attachment)) {
		outInfo = attachment.flatMap((att) => getAttachmentInfo(att, fieldMappings));
	}

	return outInfo;
};

export const getMessageStatus = (messageStatus: ESField): string => {
	if (typeof messageStatus === 'string') {
		return messageStatus;
	} else {
		if (messageStatus) {
			return 'Read';
		} else {
			return 'Unread';
		}
	}
};
